import React from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn, setIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson, InlandMarineTypes as defaultInlandMarineTypes } from '@ourbranch/lookups';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import { useStore } from 'core/store';
import { getMaskedInlandMarineTypeOptions } from 'common/helpers/farm-helpers';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import { AppliedTag } from 'common/components/farm/applied-tag';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from './collapsible-list-item';
import Grid from '@material-ui/core/Grid';
import alertIcon from 'core/assets/svg/alert-white-bold.svg';

import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './sub-risk.styles';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { hasErrorMessages } from './sub-risk.utils';

const defaultInlandMarineTypesOptions = Object.values(defaultInlandMarineTypes).map((type) => ({
  id: type.id,
  value: type.value
}));

// Utility function to strip GraphQL __typename fields to prevent false positive offer differences
const stripTypenames = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(stripTypenames);
  }
  if (obj && typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      if (key !== '__typename') {
        cleaned[key] = stripTypenames(obj[key]);
      }
    });
    return cleaned;
  }
  return obj;
};

const typesRequiringYear = ['boat', 'atv', 'golfCart', 'unmannedAircraft'];
const typesRequiringMake = ['golfCart', 'atv', 'unmannedAircraft'];
const typesRequiringModel = ['golfCart', 'atv', 'unmannedAircraft'];
const typesRequiringSerialNumber = ['boat', 'unmannedAircraft', 'atv', 'golfCart'];

const validationSchema = (state) =>
  Yup.object().shape({
    category: Yup.string().required('Item type is required'),
    name: Yup.string().required('Item name is required'),
    deductible: Yup.number().when('category', {
      is: (val) => ['scheduledGlass', 'computerHardware', 'computerSoftware'].includes(val),
      then: (schema) => schema.required('Deductible is required'),
      otherwise: (schema) => (state === 'IL' ? schema.nullable() : schema.required('Deductible is required'))
    }),
    limit: Yup.number().required('Coverage limit is required'),
    description: Yup.string().nullable().required('Description is required'),
    year: Yup.number().when('category', {
      is: (val) => typesRequiringYear.includes(val),
      then: (schema) =>
        schema
          .required('Year is required')
          .min(1900, 'Year must be 1900 or later')
          .max(new Date().getFullYear(), `Year must be ${new Date().getFullYear()} or earlier`)
    }),
    horsepower: Yup.number().when('category', {
      is: 'boat',
      then: (schema) => schema.required('Horsepower is required')
    }),
    serialNumber: Yup.string().when('category', {
      is: (val) => typesRequiringSerialNumber.includes(val),
      then: (schema) => schema.required('Serial Number is required')
    }),
    classification: Yup.string().when('category', {
      is: 'boat',
      then: (schema) => schema.required('Classification is required')
    }),
    make: Yup.string().when('category', {
      is: (val) => typesRequiringMake.includes(val),
      then: (schema) => schema.required('Make is required')
    }),
    model: Yup.string().when('category', {
      is: (val) => typesRequiringModel.includes(val),
      then: (schema) => schema.required('Model is required')
    }),
    cc: Yup.mixed().when('category', {
      is: 'atv',
      then: (schema) => schema.required('CC is required')
    }),
    weight: Yup.number().when('category', {
      is: 'unmannedAircraft',
      then: (schema) => schema.required('Weight is required')
    }),
    lengthFt: Yup.number().when('category', {
      is: 'boat',
      then: (schema) => schema.required('Length is required')
    }),
    milesPerHour: Yup.number().when('category', {
      is: 'boat',
      then: (schema) => schema.required('Miles per hour is required')
    }),
    dimensions: Yup.string().when('category', {
      is: 'boatDock',
      then: (schema) => schema.required('Dimensions are required')
    })
  });

const alertMessages = {
  atv: 'Remember to add Incidental Liability coverage when an ATV or Related Equipment is included on the policy.',
  boat: 'Remember to add Incidental Liability coverage when a boat or watercraft is included on the policy.',
  boatDock: 'Boat docks are excluded from damage caused by collapse of weight of ice, snow or sleet.',
  golfCart: 'When a golf cart is included on the policy, liability coverage must be on and the item must be scheduled.',
  unmannedAircraft: 'Remember to add Incidental Liability coverage when an Unmanned Aircraft is included on the policy.'
};

// Simple types that only need Description
const simpleTypes = [
  'coinCollection',
  'fineArts',
  'furs',
  'golfEquipment',
  'guns',
  'jewelry',
  'miscellaneousProperty',
  'musicalInstruments',
  'personalTools',
  'silverware',
  'sportingEquipment',
  'tack',
  'lawnMowersGardenTractorsAndRelatedEquipment',
  'scheduledGlass',
  'computerHardware',
  'computerSoftware'
];

const InlandMarineForm = ({ fieldName, disabled, riskType, coverages }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const items = scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items || [];
  const coverageIndex = scheduleCoverages.findIndex((c) => c.coverageType === FarmScheduleCoverageType.SPP);

  const state = getIn(values, 'farmLocations.0.address.state') || getIn(values, 'correctedAddress.state');
  const {
    offer: { selectedOption }
  } = useStore();

  const isInlandMarineCoverageEnabled =
    !appConfig.farm[state]?.risks[riskType]?.hiddenCoverages?.includes('inlandMarine');
  const brandSpecificTypes = getMaskedInlandMarineTypeOptions(
    selectedOption,
    state,
    (
      appConfig.farm[state]?.risks[riskType]?.allowedOptions?.inlandMarineItemTypes || defaultInlandMarineTypesOptions
    ).sort((a, b) => a.id.localeCompare(b.id))
  );
  const brandDeductibleOptions =
    appConfig.farm[state]?.risks[riskType]?.allowedOptions?.inlandMarineDeductible ||
    lookupsJson.farm.inlandMarineDeductible;

  if (!isInlandMarineCoverageEnabled) {
    return null;
  }

  // Auto-expand if there are any scheduled inland marine items
  const hasScheduledItems = items.length > 0;

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Inland marine coverage</div>}
        contentRight={hasScheduledItems ? <AppliedTag /> : null}
        initialOpen={hasScheduledItems}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items?.length || 0}
          emptyStateTitle="There are no items added yet"
        >
          {/* list items */}
          {items.map((item, index) => {
            const typeName = brandSpecificTypes.find((type) => type.id === item.category)?.value;
            const displayName = item.name || typeName;
            const rightSideValues = [];

            if (item.deductible) {
              rightSideValues.push({
                label: 'Deductible',
                value: `$${item.deductible?.toLocaleString('en-US') || item.deductible}`
              });
            }
            rightSideValues.push({ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` });

            const itemPath = `${fieldName}[${coverageIndex}].items[${index}]`;
            const category = item.category;
            const isIL = state === 'IL';

            return (
              <CollapsibleListItem
                key={`item-${index}`}
                title={
                  <div className={classes.itemTitleWrapper}>
                    <div>{displayName}</div>
                    <div className={classes.itemTypeLabel}>{typeName}</div>
                  </div>
                }
                rightSideValues={rightSideValues}
                alertMessage={alertMessages[item.category] ?? null}
                onDelete={() => {
                  const newItems = items.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    stripTypenames(
                      getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP)
                    ) || [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.SPP,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);
                }}
                disabled={disabled}
              >
                <Field
                  xs={12}
                  type="string"
                  id={`${itemPath}.name`}
                  name={`${itemPath}.name`}
                  mode="dark"
                  label="Item Name"
                  disabled={disabled}
                />

                {category === 'golfCart' && (
                  <>
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'atv' && (
                  <>
                    <Field
                      xs={4}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={4}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={4}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="select"
                      id={`${itemPath}.extendedDetails.cc`}
                      name={`${itemPath}.extendedDetails.cc`}
                      mode="dark"
                      label="CC"
                      options={lookupsJson.farm.cc}
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'unmannedAircraft' && (
                  <>
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.weight`}
                      name={`${itemPath}.extendedDetails.weight`}
                      mode="dark"
                      label="Weight (lbs)"
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'boat' && (
                  <>
                    <Field
                      xs={6}
                      type="select"
                      id={`${itemPath}.extendedDetails.classification`}
                      name={`${itemPath}.extendedDetails.classification`}
                      mode="dark"
                      label="Classification"
                      options={lookupsJson.farm.boatClassifications}
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.horsepower`}
                      name={`${itemPath}.extendedDetails.horsepower`}
                      mode="dark"
                      label="Horsepower"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.lengthFt`}
                      name={`${itemPath}.extendedDetails.lengthFt`}
                      mode="dark"
                      label="Length (ft)"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.milesPerHour`}
                      name={`${itemPath}.extendedDetails.milesPerHour`}
                      mode="dark"
                      label="Miles Per Hour"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'boatDock' && (
                  <Field
                    xs={12}
                    type="string"
                    id={`${itemPath}.extendedDetails.dimensions`}
                    name={`${itemPath}.extendedDetails.dimensions`}
                    mode="dark"
                    label="Dimensions"
                    disabled={disabled}
                  />
                )}

                <Field
                  xs={12}
                  type="textarea"
                  id={`${itemPath}.description`}
                  name={`${itemPath}.description`}
                  mode="dark"
                  label={category === 'unmannedAircraft' ? 'Description of farming operations' : 'Description'}
                  disabled={disabled}
                />

                {!isIL && (
                  <Field
                    xs={4}
                    type="select"
                    id={`${itemPath}.deductible`}
                    name={`${itemPath}.deductible`}
                    mode="dark"
                    label="Deductible"
                    options={lookupsJson.farm.inlandFloodDeductible}
                    disabled={disabled}
                  />
                )}
                <Field
                  xs={isIL ? 6 : 4}
                  type="numeric"
                  id={`${itemPath}.limit`}
                  name={`${itemPath}.limit`}
                  mode="dark"
                  label="Coverage Limit"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                />
                <BrandFormField
                  xs={isIL ? 6 : 4}
                  id={`${itemPath}.extendedDetails.deductibleAllOther`}
                  name={`${itemPath}.extendedDetails.deductibleAllOther`}
                  type="select"
                  options={brandDeductibleOptions}
                  mode="dark"
                  label="All peril deductible"
                  disabled={disabled}
                  riskType={riskType}
                  state={state}
                  coverages={coverages}
                />
              </CollapsibleListItem>
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                category: undefined,
                name: undefined,
                deductible: state === 'IL' ? null : 250,
                limit: undefined,
                description: undefined,
                deductibleAllOther: undefined
              }}
              validateOnChange={true}
              validateOnBlur={true}
              validationSchema={validationSchema(state)}
              onSubmit={async (val, { resetForm }) => {
                const parentErrors = await validateForm();
                // Exclude scheduleCoverages error since adding this item is what resolves it (GitHub issue #29426)
                const errorsExcludingScheduleCoverages = setIn(parentErrors, fieldName, undefined);
                if (hasErrorMessages(errorsExcludingScheduleCoverages)) {
                  setErrors(parentErrors);
                  setTouched(setTouchedFromErrors(parentErrors), false);
                  setShowValidationErrorsModal(true);
                  return;
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });
                const { category, name, deductible, description, limit, ...rest } = item;
                const newItem = {
                  category,
                  name,
                  deductible,
                  description,
                  limit,
                  ...(Object.keys(rest).length > 0
                    ? {
                        extendedDetails: {
                          ...rest
                        }
                      }
                    : {})
                };
                const currentScheduleCoverages =
                  stripTypenames(
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP)
                  ) || [];
                const currentSPPItems =
                  stripTypenames(
                    getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items
                  ) || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.SPP,
                    items: [...currentSPPItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);

                resetForm();
              }}
            >
              {({ values: formValues, submitForm }) => {
                const isIL = state === 'IL';
                const category = formValues.category;

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    {/* Top Row: Type of Item + Item Name */}
                    <Field
                      xs={6}
                      type="select"
                      id="category"
                      name="category"
                      mode="dark"
                      label="Type of item"
                      options={brandSpecificTypes}
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id="name"
                      name="name"
                      mode="dark"
                      label="Item Name"
                      disabled={disabled}
                      helperText="Give this item a small name for reference"
                    />

                    {/* Type-specific fields */}
                    {category && simpleTypes.includes(category) && (
                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Description"
                        disabled={disabled}
                      />
                    )}

                    {category === 'golfCart' && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          id="year"
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="make"
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="model"
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="serialNumber"
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'atv' && (
                      <>
                        <Field
                          xs={4}
                          type="numeric"
                          id="year"
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={4}
                          type="string"
                          id="make"
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={4}
                          type="string"
                          id="model"
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="serialNumber"
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="select"
                          id="cc"
                          name="cc"
                          mode="dark"
                          label="CC"
                          options={lookupsJson.farm.cc}
                          disabled={disabled}
                        />
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'unmannedAircraft' && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          id="year"
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="make"
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="model"
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="serialNumber"
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="weight"
                          name="weight"
                          mode="dark"
                          label="Weight (lbs)"
                          disabled={disabled}
                        />
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description of farming operations"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'boat' && (
                      <>
                        <Field
                          xs={6}
                          type="select"
                          id="classification"
                          name="classification"
                          mode="dark"
                          label="Classification"
                          options={lookupsJson.farm.boatClassifications}
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="horsepower"
                          name="horsepower"
                          mode="dark"
                          label="Horsepower"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="year"
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="lengthFt"
                          name="lengthFt"
                          mode="dark"
                          label="Length (ft)"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="milesPerHour"
                          name="milesPerHour"
                          mode="dark"
                          label="Miles Per Hour"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="serialNumber"
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'boatDock' && (
                      <>
                        <Field
                          xs={12}
                          type="string"
                          id="dimensions"
                          name="dimensions"
                          mode="dark"
                          label="Dimensions"
                          disabled={disabled}
                        />
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {/* Bottom Row: Deductible + Coverage Limit + All Peril Deductible */}
                    {category && (
                      <>
                        {!isIL && (
                          <Field
                            xs={4}
                            type="select"
                            id="deductible"
                            name="deductible"
                            mode="dark"
                            label="Deductible"
                            options={lookupsJson.farm.inlandFloodDeductible}
                            disabled={disabled}
                          />
                        )}
                        <Field
                          xs={isIL ? 6 : 4}
                          type="numeric"
                          id="limit"
                          name="limit"
                          mode="dark"
                          label="Coverage Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />
                        <BrandFormField
                          xs={isIL ? 6 : 4}
                          id="deductibleAllOther"
                          name="deductibleAllOther"
                          type="select"
                          options={brandDeductibleOptions}
                          mode="dark"
                          label="All peril deductible"
                          disabled={disabled}
                          riskType={riskType}
                          state={state}
                          coverages={coverages}
                        />
                      </>
                    )}

                    {category && (
                      <Grid container alignItems="center" className={classes.singleBottom}>
                        {alertMessages[category] && (
                          <div className={classes.alert}>
                            <img src={alertIcon} className={classes.alertIcon} alt="Alert Icon" />
                            <div className={classes.alertMessage}>{alertMessages[category]}</div>
                          </div>
                        )}
                        <Button
                          type="button"
                          mode="big"
                          variant="contained"
                          color="secondary"
                          className={classes.button}
                          disabled={disabled}
                          onClick={submitForm}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

InlandMarineForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  riskType: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

InlandMarineForm.defaultProps = {
  disabled: false
};

export default observer(InlandMarineForm);
